import { Report } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { useState } from 'react';
import type { ReportEntityType } from '@/types';
import ReportDialog from './report-dialog';

export default function ReportContentButton({
  entityId,
  entityType,
  onReport,
  show = true,
  variant = 'floating',
}: {
  entityId: string;
  entityType: ReportEntityType;
  onReport: (id: string, entityType: ReportEntityType, message: string) => Promise<boolean>;
  show?: boolean;
  variant?: 'floating' | 'inline';
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleReport = async (message: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      const result = await onReport(entityId, entityType, message);
      return result;
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (dialogOpen) {
      setDialogOpen(false);
      setAnchorEl(null);
      return;
    }

    setAnchorEl(event.currentTarget);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setAnchorEl(null);
  };

  if (!show) return null;

  const isFloating = variant === 'floating';
  const isInline = variant === 'inline';

  return (
    <Box
      sx={
        isFloating
          ? {
              position: 'fixed',
              bottom: { xs: 70, sm: 78 },
              right: 24,
              zIndex: 1000,
            }
          : {
              display: 'inline-flex',
            }
      }>
      <Button
        onClick={handleToggle}
        variant={dialogOpen ? 'outlined' : isInline ? 'text' : 'contained'}
        size={isInline ? 'small' : 'small'}
        startIcon={<Report />}
        sx={
          isFloating
            ? {
                backgroundColor: dialogOpen ? 'transparent' : 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                borderColor: dialogOpen ? 'rgba(255, 255, 255, 0.5)' : 'transparent',
                textTransform: 'none',
                px: 1.5,
                py: 0.75,
                minWidth: 'auto',
                borderRadius: 6,
                boxShadow: (theme) => (dialogOpen ? theme.shadows[8] : theme.shadows[4]),
                backdropFilter: 'blur(10px)',
                transform: dialogOpen ? 'scale(1.05)' : 'scale(1)',
                transition: 'all 0.2s ease-in-out',
                '& .MuiButton-startIcon': {
                  marginRight: '8px',
                },
                '&:hover': {
                  backgroundColor: dialogOpen ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.8)',
                  boxShadow: (theme) => theme.shadows[8],
                },
              }
            : {
                color: 'text.secondary',
                fontSize: '0.65rem',
                textTransform: 'none',
                px: 0.75,
                py: 0.25,
                minWidth: 'auto',
                minHeight: 'auto',
                borderRadius: 1,
                '& .MuiButton-startIcon': {
                  marginRight: '4px',
                },
                '&:hover': {
                  backgroundColor: 'action.hover',
                  color: 'text.primary',
                },
              }
        }>
        <Typography variant="caption">Melden</Typography>
      </Button>
      <ReportDialog
        open={dialogOpen}
        onClose={handleClose}
        onSubmit={handleReport}
        entityType={entityType}
        isLoading={isLoading}
        anchorEl={anchorEl}
        presentation={isInline ? 'popover-below' : 'popover'}
      />
    </Box>
  );
}
